import { type UseQueryResult } from '@tanstack/react-query';
import BaseMessageBarError from '../MessageBar/Error';
import { UiHStack, UiSpinner } from '@/lib/ui';
import { type ReactNode } from 'react';
interface QueryContainerProps<T> {
  query: UseQueryResult<T>
  loadingComponent?: React.ReactElement
  errorMessage?: string
  placeHolder?: ReactNode
  children: (data: T) => React.ReactElement
  onError?: (error: any) => React.ReactNode
}

export function StandardSpinner() {
  return (
    <UiHStack justifyContent={'flex-start'} overflow={'hidden'}>
      <UiSpinner size={'md'} color={'primary.500'} thickness='2px' />
    </UiHStack>
  );
}

export function QueryContainer<T>({
  query,
  loadingComponent,
  children,
  errorMessage,
  placeHolder,
  onError = undefined,
}: QueryContainerProps<T>) {
  const { data, isFetching, error } = query;

  // We need to use isFetching here since isLoading is always true even if the query is not enabled.
  if (isFetching) {
    return loadingComponent ?? (
      <StandardSpinner />
    );
  }

  // Only show the error if there is an actual error.
  if (error) {
    if (onError) {
      return onError(error);
    }
    return (
      <BaseMessageBarError>
        {errorMessage ?? ((error as Error).message ?? 'Failed to load data.')}
      </BaseMessageBarError>
    );
  }

  // Only render the children if there is actual data returned.
  if (data !== null && data !== undefined) {
    return children(data);
  } else {
    return placeHolder ?? null;
  }
}

export default QueryContainer;
